import { useMemo } from 'react';

import {
  ageFromBucket,
  estimateWorkoutCalories,
  resolveAthleteEnergy,
  sessionEnergyReadout,
  type MovementPattern,
  type Workout,
  type WorkoutEnergyReadout,
} from '@heraion/shared';

import type { LoggerProfile } from './hostDeps';

type PatternsBySlug = Readonly<Record<string, MovementPattern | null | undefined>>;
type EnergyProfile = Pick<LoggerProfile, 'bodyweightKg' | 'sex' | 'ageBucket'>;

/** A saved session's energy readout, read from its STORED fields (HC-8 B2c):
 *  the stored estimate and the cardio heart rate, not a live re-estimate.
 *  Shared by the logger's past-session edit and the summary a past session
 *  opens on (#318). */
export function storedEnergyReadout(
  w: Workout,
  patternsBySlug: PatternsBySlug,
  profile: EnergyProfile,
): WorkoutEnergyReadout {
  const readout = sessionEnergyReadout(w, {
    ageYears: ageFromBucket(profile.ageBucket),
    patternsBySlug,
  });
  if (readout.estimate?.method !== 'keytel') return readout;
  // LEGAL-5 ripiego (2026-09-09): a Keytel estimate is fed by a Health
  // Connect heart rate, so recompute the SAME estimator with HR stripped. `w` is the
  // assembled Workout (session + LogSet rows), so the raw per-set
  // ingredients ARE here, unlike the thin Session envelope web's
  // session-detail view reads (which gets no ripiego).
  const metOnly = estimateWorkoutCalories(
    w,
    patternsBySlug,
    resolveAthleteEnergy(profile),
    { includeHr: false },
  );
  return { ...readout, metFallbackKcal: metOnly?.kcal ?? null };
}

/** The same readout for a surface outside the logger. Null until the
 *  workout is loaded. */
export function useStoredEnergyReadout(
  w: Workout | null,
  patternsBySlug: PatternsBySlug,
  profile: EnergyProfile,
): WorkoutEnergyReadout | null {
  return useMemo(
    () => (w ? storedEnergyReadout(w, patternsBySlug, profile) : null),
    [w, patternsBySlug, profile],
  );
}
